import type { RefinanceAllocation } from "./refinance-allocation";

export type AllocationMarkerId = "headroom" | "payout";

/** A point on the bar where collecting one more krona starts to cost the customer. */
export interface AllocationMarker {
	id: AllocationMarkerId;
	/** Where the marker sits, as an amount from the start of the track. */
	offset: number;
	/** `offset` as a fraction of the track, ready for a CSS percentage. */
	ratio: number;
}

/**
 * The boundaries inside the gathered region: past `headroom` the loan grows,
 * past `payout` the cash paid out shrinks. A boundary sitting at the very start
 * of the track has nothing to its left to divide, so it is dropped.
 */
export const resolveAllocationMarkers = (
	allocation: RefinanceAllocation
): AllocationMarker[] => {
	const { internal, mandatory, voluntary, headroom, payout, maxLoanAmount } =
		allocation;

	if (maxLoanAmount <= 0) return [];

	const headroomOffset = internal + mandatory.capacity + voluntary.capacity;
	const candidates: { id: AllocationMarkerId; offset: number }[] = [];

	if (headroom.capacity > 0) {
		candidates.push({ id: "headroom", offset: headroomOffset });
	}

	if (payout.capacity > 0) {
		candidates.push({
			id: "payout",
			offset: headroomOffset + headroom.capacity,
		});
	}

	return candidates
		.filter(({ offset }) => offset > 0)
		.map(({ id, offset }) => ({ id, offset, ratio: offset / maxLoanAmount }));
};
